import Hls from 'hls.js';
import { useEffect, useRef, useState } from 'react';

/**
 * What a listener sees when the host is broadcasting from the studio.
 *
 * ## Why this is not the dock
 *
 * In catalogue mode the room tells the dock which track to play and the dock
 * fetches it from the CDN, which is why a broadcast survives navigating away.
 * In studio mode there is no track to fetch: the audio the host mixed and the
 * picture they composited arrive together as one HLS stream from the ingest
 * server, and it has a picture, which the dock has nowhere to put.
 *
 * So this element owns playback for the duration, and `RoomAudio` stands down -
 * see the note there. Two players fed from the same room would mean hearing the
 * track twice, a second or so apart.
 *
 * ## Muted until asked
 *
 * Browsers refuse to autoplay audible media, and a video that silently fails to
 * start looks like a broken stream rather than a policy. So it starts muted and
 * playing - which *is* allowed, and which proves the stream is live - with the
 * sound one click away.
 *
 * ## The stream can be live before it is playable
 *
 * There is a window after the host presses start where the room says stream
 * mode and the ingest server has not cut a segment yet, so the playlist 404s.
 * That is normal and brief, and it is why a network error here retries rather
 * than reporting a failure at somebody who has done nothing wrong.
 */
export function StreamPlayer({ url, listening }: { url: string; listening: boolean }) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [muted, setMuted] = useState(true);
  const [waiting, setWaiting] = useState(true);

  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    setWaiting(true);

    /*
     * hls.js first, native second - and that order is the whole fix.
     *
     * Asking `canPlayType('application/vnd.apple.mpegurl')` first looks right
     * and is a trap: Chromium answers `"maybe"`, which is truthy, and then
     * fails to demux the playlist it was just handed. The symptom is a video
     * element that reports `readyState 4` and a decoded 960x540 frame while
     * sitting at `currentTime 0` forever, with
     * `DEMUXER_ERROR_COULD_NOT_PARSE` hidden in `video.error`.
     *
     * Where native HLS is genuinely the better engine - iOS Safari, which has
     * no MSE at all - `Hls.isSupported()` is false, so the fallback below is
     * reached for exactly the platforms that need it.
     */
    if (!Hls.isSupported()) {
      if (!video.canPlayType('application/vnd.apple.mpegurl')) return undefined;

      video.src = url;

      return () => {
        video.removeAttribute('src');
        video.load();
      };
    }

    const hls = new Hls({
      /*
       * Tuned for live, unlike `HlsEngine` which serves a fixed-length track.
       *
       * `liveSyncDurationCount: 3` keeps the playhead about three segments from
       * the edge - close enough that the host's "hello" is not ten seconds
       * stale, far enough that one slow segment does not stall. `lowLatencyMode`
       * uses the partial segments MediaMTX publishes.
       */
      lowLatencyMode: true,
      liveSyncDurationCount: 3,
      backBufferLength: 30,
      enableWorker: true,
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      switch (data.type) {
        case Hls.ErrorTypes.NETWORK_ERROR:
          // Usually the playlist not existing yet - see above. Retry rather
          // than tell somebody their connection is broken when it is not.
          hls.startLoad();
          break;

        case Hls.ErrorTypes.MEDIA_ERROR:
          hls.recoverMediaError();
          break;

        default:
          hls.destroy();
      }
    });

    hls.loadSource(url);
    hls.attachMedia(video);

    return () => hls.destroy();
  }, [url]);

  /*
   * Sound follows the room's listen state.
   *
   * A listener who pressed "Listen live" has made the gesture browsers require,
   * so unmuting from that is allowed. One who has not gets the picture and
   * silence, which is the honest default for a page they merely scrolled to.
   */
  useEffect(() => {
    const video = ref.current;

    if (!video) return;

    video.muted = !listening || muted;
    void video.play().catch(() => {
      // Autoplay refused even muted, which some configurations do. The poster
      // state below is what the listener sees, and their next click starts it.
    });
  }, [listening, muted]);

  return (
    <div
      className="relative w-full overflow-hidden rounded-[20px] border border-[#26264e] bg-black"
      data-stream-player
    >
      <video
        ref={ref}
        // `playsInline` or iOS takes the stream fullscreen the moment it plays,
        // which throws the listener out of the page they were reading.
        playsInline
        autoPlay
        muted
        className="block aspect-video w-full"
        data-stream-video
        onPlaying={() => setWaiting(false)}
        onWaiting={() => setWaiting(true)}
      />

      {waiting && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/60">
          <span className="meta-caps text-ghost">connecting…</span>
        </div>
      )}

      {listening && muted && !waiting && (
        <button
          type="button"
          onClick={() => setMuted(false)}
          data-stream-unmute
          className="absolute bottom-4 left-4 rounded-[var(--radius-pill)] bg-ink px-4 py-2 text-[13px] font-bold text-app transition-opacity hover:opacity-90"
        >
          Unmute
        </button>
      )}
    </div>
  );
}
